import moment from 'moment';

// Returns the date part in 'YYYY-MM-DD' format from a date string in ISO format
export function parseDateStringYyyyMmDd(dateString) {
  return dateString.split('T')[0];
}

export function convertDateToISOString(date) {
  return new Date(date).toISOString();
}

export function parseDateStringYyyyMmDdNoBinder(dateString) {
  const yyyyMmDd = dateString.split('T')[0];
  return yyyyMmDd.replace(/-/g, ''); // Remove hyphens
}

export function convertDateTimeToReadFormat(datetimeStr) {
  // Split the string by the 'T' to separate date and time
  const [date, time] = datetimeStr.split('T');

  // Split the time by the ':' and take only the first two segments (hours and minutes)
  const [hours, minutes] = time.split(':');

  // Concatenate the date and time with a space and return it
  return `${date} ${hours}:${minutes}`;
}

export function toLocalTimeString(utcDateString) {
  const date = new Date(utcDateString);
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // Enforce 24-hour format
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  const formatter = new Intl.DateTimeFormat('en-CA', options); // Using Canadian English to ensure YYYY-MM-DD format
  const parts = formatter.formatToParts(date);
  let year, month, day, hour, minute;

  parts.forEach((part) => {
    if (part.type === 'year') {
      year = part.value;
    } else if (part.type === 'month') {
      month = part.value;
    } else if (part.type === 'day') {
      day = part.value;
    } else if (part.type === 'hour') {
      hour = part.value;
    } else if (part.type === 'minute') {
      minute = part.value;
    }
  });

  return `${year}-${month}-${day} ${hour}:${minute}`; // Constructing the date in the desired format
}

/**
 * Converts a GMT timestamp string to Europe/Berlin time in the format "YYYY-MM-DD HH:mm:ss".
 *
 * @param {string} gmtTimestamp - A timestamp in GMT (e.g. "2025-02-22 09:17:33").
 * @returns {string} The formatted date/time in Europe/Berlin time.
 */
export function convertGMTToAnotherTimezone(gmtTimestamp, timeZone) {
  if (!gmtTimestamp) {
    throw new Error('Invalid timestamp input');
  }

  // Remove " UTC" if present and trim to milliseconds precision
  const cleanedTimestamp = gmtTimestamp.replace(' UTC', '').split('.')[0];

  // Convert to ISO 8601 format
  const isoString = cleanedTimestamp.replace(' ', 'T') + 'Z';

  const date = new Date(isoString);

  if (isNaN(date.getTime())) {
    throw new Error(`Invalid Date: ${gmtTimestamp}`);
  }

  // Use Intl.DateTimeFormat with formatToParts to get individual date parts in the target timezone.
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  // Get the individual parts (year, month, day, etc.)
  const parts = formatter.formatToParts(date);
  let year, month, day, hour, minute, second;
  for (const part of parts) {
    if (part.type === 'year') year = part.value;
    if (part.type === 'month') month = part.value;
    if (part.type === 'day') day = part.value;
    if (part.type === 'hour') hour = part.value;
    if (part.type === 'minute') minute = part.value;
    if (part.type === 'second') second = part.value;
  }

  // Return the formatted string in "YYYY-MM-DD HH:mm:ss" format
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
}

export function getFormattedDateTime() {
  const now = new Date();
  const date = now.toISOString().split('T')[0];
  const time = now.toTimeString().split(' ')[0].replace(/:/g, '-');
  return `${date}_${time}`;
}

export function add1DayAndConvertDateToISOString(date) {
  if (date) {
    const dateObj = new Date(date);
    dateObj.setDate(dateObj.getDate() + 1); // Adding 1 day
    return dateObj.toISOString();
  }
}

export function formatIsoDateInTimezone(isoDate, timeZone) {
  return new Intl.DateTimeFormat('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: timeZone, // Convert to Europe/Berlin timezone
  }).format(new Date(isoDate));
}

/**
 * Converts a Firebase timestamp (e.g. "Sun, 02 Feb 2025 12:45:05 GMT") to the specified timezone
 *
 * @param {string} firebaseTimestamp - A timestamp from Firebase in the format "Sun, 02 Feb 2025 12:45:05 GMT"
 * @param {string} timeZone - The timezone to convert to (e.g. "Europe/Berlin")
 * @returns {string} The formatted date/time in the specified timezone
 */
export function convertFirebaseTimestampToTimezone(firebaseTimestamp, timeZone) {
  if (!firebaseTimestamp) {
    return 'N/A';
  }

  try {
    // Create a Date object from the Firebase timestamp
    const date = new Date(firebaseTimestamp);

    if (isNaN(date.getTime())) {
      return firebaseTimestamp; // Return original if parsing fails
    }

    // Use Intl.DateTimeFormat with formatToParts to get individual date parts in the target timezone
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    // Get the individual parts (year, month, day, etc.)
    const parts = formatter.formatToParts(date);
    let year, month, day, hour, minute, second;
    for (const part of parts) {
      if (part.type === 'year') year = part.value;
      if (part.type === 'month') month = part.value;
      if (part.type === 'day') day = part.value;
      if (part.type === 'hour') hour = part.value;
      if (part.type === 'minute') minute = part.value;
      if (part.type === 'second') second = part.value;
    }

    // Return the formatted string in "YYYY-MM-DD HH:mm:ss" format
    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  } catch (error) {
    console.error('Error converting Firebase timestamp:', error);
    return firebaseTimestamp; // Return original on error
  }
}

/**
 * Gets a user-friendly timezone display with abbreviation and offset
 *
 * @param {string} timeZone - The timezone identifier (e.g. "Europe/Berlin")
 * @returns {string} User-friendly timezone display (e.g. "CET (+01:00)")
 */
export function getUserFriendlyTimezoneDisplay(timeZone) {
  try {
    const now = new Date();

    // Get timezone abbreviation
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timeZone,
      timeZoneName: 'short',
    });

    const parts = formatter.formatToParts(now);
    const timeZoneName = parts.find((part) => part.type === 'timeZoneName')?.value || timeZone;

    // Get offset
    const offsetFormatter = new Intl.DateTimeFormat('en', {
      timeZone: timeZone,
      timeZoneName: 'longOffset',
    });
    const offsetParts = offsetFormatter.formatToParts(now);
    const offset = offsetParts.find((part) => part.type === 'timeZoneName')?.value || '';

    return `${timeZoneName} (${offset})`;
  } catch (error) {
    console.error('Error getting timezone display:', error);
    return timeZone;
  }
}

/**
 * Formats a date for datetime-local input using the specified timezone
 *
 * @param {Date} date - The date to format
 * @param {string} timeZone - The timezone to use for formatting
 * @returns {string} Formatted date string for datetime-local input (YYYY-MM-DDTHH:MM)
 */
export function formatDateForDatetimeLocal(date, timeZone) {
  try {
    // Use existing formatIsoDateInTimezone function
    const formattedDate = formatIsoDateInTimezone(date.toISOString(), timeZone);

    // Convert from "DD.MM.YYYY, HH:mm:ss" to "YYYY-MM-DDTHH:MM" format
    const [datePart, timePart] = formattedDate.split(', ');
    const [day, month, year] = datePart.split('.');
    const [hour, minute] = timePart.split(':');

    return `${year}-${month}-${day}T${hour}:${minute}`;
  } catch (error) {
    console.error('Error formatting date for datetime-local input:', error);
    // Fallback to UTC
    return date.toISOString().slice(0, 16);
  }
}

/**
 * Converts a datetime-local input value to an ISO string with timezone information
 * This function treats the input as being in the specified timezone and converts it to UTC
 *
 * @param {string} datetimeLocalValue - The value from a datetime-local input (e.g. "2025-05-17T13:42")
 * @param {string} timeZone - The timezone to interpret the local time in (e.g. "Europe/Berlin")
 * @returns {string} ISO string in UTC (e.g. "2025-05-17T11:42:00.000Z")
 */
export function convertDatetimeLocalToISO(datetimeLocalValue, timeZone) {
  if (!datetimeLocalValue) {
    return '';
  }

  try {
    // Parse the datetime-local value
    const [datePart, timePart] = datetimeLocalValue.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);

    // Create a date object representing this time in the target timezone
    // We use a proven approach: create the date and use Intl.DateTimeFormat to handle timezone conversion

    // Create a date in UTC with the same numbers
    const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));

    // Get what this UTC time looks like in the target timezone
    const formatter = new Intl.DateTimeFormat('de-DE', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    const formattedInTargetTz = formatter.format(utcDate);
    const targetTzDate = new Date(formattedInTargetTz + 'Z');

    // Calculate the offset and adjust
    const offset = utcDate.getTime() - targetTzDate.getTime();
    const correctedDate = new Date(utcDate.getTime() + offset);

    return correctedDate.toISOString();
  } catch (error) {
    console.error('Error converting datetime-local to ISO:', error);
    // Fallback: treat as local timezone
    return new Date(datetimeLocalValue).toISOString();
  }
}

/**
 * Simplified function to convert datetime-local to ISO with proper timezone handling
 * @param {string} datetimeLocalValue - The datetime-local input value
 * @param {string} timeZone - The target timezone
 * @returns {string} ISO string with timezone information
 */
export function datetimeLocalToISOWithTimezone(datetimeLocalValue, timeZone) {
  if (!datetimeLocalValue) {
    return '';
  }

  try {
    // Parse the input: "2025-05-17T13:42"
    const [datePart, timePart] = datetimeLocalValue.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);

    // Create a date object representing this time in the specified timezone
    // We'll use the approach of creating the date and then adjusting for timezone

    // First, create the date as if it's in UTC
    const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));

    // Get what this UTC time would be in the target timezone
    const formatter = new Intl.DateTimeFormat('de-DE', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    const targetTimeString = formatter.format(utcDate);
    const targetTime = new Date(targetTimeString + 'Z');

    // Calculate the difference
    const diff = utcDate.getTime() - targetTime.getTime();

    // Apply the difference to get the correct UTC time
    const correctUtcTime = new Date(utcDate.getTime() + diff);

    return correctUtcTime.toISOString();
  } catch (error) {
    console.error('Error in datetimeLocalToISOWithTimezone:', error);
    // Fallback
    return new Date(datetimeLocalValue).toISOString();
  }
}

export const formatDateTime = (date) => {
  if (!date) {
    return '';
  }
  return moment(date).format('MM.DD.YYYY HH:mm');
};
